/**
 * PDF generation helpers for html2pdf.js (html2canvas + jsPDF).
 *
 * Centralises the fixes for the recurring PDF rendering issues:
 *  - html2canvas does NOT reliably honour flex/grid utilities → callers
 *    should use explicit block/pixel positioning inside the printed area.
 *  - Custom web fonts may not be loaded yet when the snapshot fires →
 *    `waitForFontsReady()` resolves only after document.fonts.ready + a
 *    500 ms safety delay.
 *  - External images (CORS / network) sometimes 404 in headless capture
 *    → `inlineImagesAsBase64(el)` rewrites every <img> with a fetched
 *    base64 data URI before the snapshot fires.
 *  - Page splits across cards/rows look ugly → CSS rules `.pdf-no-break`
 *    + `<section>`/`<tr>` already declared on all voucher bodies.
 *
 * Usage:
 *   const opt = buildHtml2PdfOptions({ filename, marginTopBottom: 10 });
 *   await prepareElementForPdf(element);
 *   await html2pdf().set(opt).from(element).save();
 */

export const PDF_TARGET_WIDTH_PX = 794; // A4 width @ 96dpi
export const PDF_FONT_SAFETY_MS = 500;

/** Resolves when `document.fonts.ready` settles + a 500 ms safety delay. */
export async function waitForFontsReady(extraDelayMs = PDF_FONT_SAFETY_MS) {
  try {
    if (document?.fonts?.ready) {
      await document.fonts.ready;
    }
  } catch (_) {
    // Fonts API unavailable — fall through to the safety delay.
  }
  await new Promise((r) => setTimeout(r, extraDelayMs));
}

/** Fetch a remote image and return a data URI; returns null on failure. */
async function fetchImageAsDataUri(src) {
  try {
    const res = await fetch(src, { credentials: 'include', mode: 'cors' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (_) {
    return null;
  }
}

/**
 * Walk every <img> inside `root` and convert non-data-uri sources to
 * inline base64. Modifies the DOM in place. Failures are silent — the
 * original src stays so the browser preview is unaffected.
 */
export async function inlineImagesAsBase64(root) {
  if (!root) return;
  const imgs = Array.from(root.querySelectorAll('img'));
  await Promise.all(
    imgs.map(async (img) => {
      const src = img.getAttribute('src') || '';
      if (!src || src.startsWith('data:')) return;
      const dataUri = await fetchImageAsDataUri(src);
      if (dataUri) {
        img.setAttribute('src', dataUri);
        img.setAttribute('crossorigin', 'anonymous');
      }
    })
  );
}

/**
 * Prep step before invoking html2pdf — runs the font + image fixes in
 * parallel. Call once per export.
 */
export async function prepareElementForPdf(element) {
  if (!element) return;
  await Promise.all([waitForFontsReady(), inlineImagesAsBase64(element)]);
}

/**
 * Build the html2pdf option bundle our app uses everywhere. Keeps the
 * scale/window/pagebreak settings consistent across vouchers + itinerary.
 */
export function buildHtml2PdfOptions({
  filename,
  marginTopBottom = 8,
  marginLeftRight = 8,
  pageMargin = null,
  scale = 2,
} = {}) {
  return {
    margin: pageMargin || [marginTopBottom, marginLeftRight, marginTopBottom, marginLeftRight],
    filename: filename || 'document.pdf',
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: {
      scale,
      useCORS: true,
      allowTaint: false,
      letterRendering: true,
      backgroundColor: '#ffffff',
      logging: false,
      scrollX: 0,
      scrollY: 0,
      windowWidth: PDF_TARGET_WIDTH_PX,
    },
    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait', compress: true },
    // pagebreak.mode: `avoid-all` forces html2pdf to AGGRESSIVELY split at
    // .pdf-no-break / section / tr boundaries instead of just trying. Combined
    // with `css` (honour `page-break-*` CSS) + `legacy` (compat fallback) it
    // produces the cleanest splits we can get from html2pdf.js.
    pagebreak: {
      mode: ['avoid-all', 'css', 'legacy'],
      avoid: ['.pdf-no-break', 'section', 'tr', '.break-inside-avoid', 'li', 'figure'],
    },
  };
}
